/**
 * QBit Ecosystem - Generic Blockchain Actions & Cashier SDK
 * 统一链上存证与快捷收银高级 SDK 模块
 * 
 * 动态 DOM 注入，零框架依赖，完美支持 React/Vue/Vanilla HTML 应用一键接入。
 */

import { getConfig, CHAIN_NETWORKS } from '@quantabit/sdk-config';

// 默认主网与备用 RPC 节点池 (做最后的 Fallback)
const DEFAULT_RPC_NODES = [
  'https://api.mainnet.qbitchain.io',
  'https://api.testnet.qbitchain.io',
];

/**
 * QBit 全局生态配置中心
 */
export class QBitEcosystem {
  static apiKey = '';
  static environment = 'production';
  static rpcNodes = [];
  static activeNodeIndex = 0;

  /**
   * 初始化 SDK
   * @param {Object} options 
   */
  static init(options = {}) {
    this.apiKey = options.apiKey || '';
    this.environment = options.environment || 'production';
    
    // 初始化节点池，优先从 sdk-config 中读取当前激活网络
    const nodes = [];
    if (options.networkEndpoint) {
      nodes.push(options.networkEndpoint);
    }
    try {
      const config = getConfig();
      if (config.chainEndpoint) {
        nodes.push(config.chainEndpoint.replace(/\/$/, ''));
      }
      if (config.apiFullUrl) {
        nodes.push(config.apiFullUrl.replace(/\/$/, ''));
      }
    } catch (e) {
      // 捕获可能的环境加载异常
    }
    
    // 注入默认 Fallback 节点
    DEFAULT_RPC_NODES.forEach(node => nodes.push(node));
    
    // 节点池去重并过滤空值
    this.rpcNodes = Array.from(new Set(nodes.filter(Boolean)));
    this.activeNodeIndex = 0;
    
    console.log(`[QBit SDK] 初始化成功。环境: ${this.environment}, 节点池:`, this.rpcNodes);
  }

  /**
   * 带自动故障漂移的 RPC 请求封装 (Failover RPC)
   */
  static async requestNode(path, options = {}, retries = 3) {
    for (let i = 0; i < retries; i++) {
      const activeNode = this.rpcNodes[this.activeNodeIndex];
      const url = `${activeNode}${path}`;
      try {
        const res = await fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'X-QBit-ApiKey': this.apiKey,
            ...(options.headers || {})
          }
        });
        if (res.ok) {
          return await res.json();
        }
        throw new Error(`RPC 响应失败 Status: ${res.status}`);
      } catch (err) {
        console.warn(`[QBit SDK] 节点请求失败: ${activeNode}。正在尝试自动故障漂移...`, err);
        // 自动漂移到下一个节点
        this.activeNodeIndex = (this.activeNodeIndex + 1) % this.rpcNodes.length;
      }
    }
    throw new Error(`[QBit SDK] 所有 RPC 节点均不可达。`);
  }
}

/**
 * 全局通知飘窗控制 (Toast Notification System)
 */
export class QBitToast {
  static container = null;

  static ensureContainer() {
    if (this.container) return;
    this.container = document.createElement('div');
    this.container.className = 'qbit-toast-container';
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '1.5rem',
      right: '1.5rem',
      zIndex: '10000',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      pointerEvents: 'none'
    });
    document.body.appendChild(this.container);
  }

  /**
   * 弹出 Toast 消息
   * @param {Object} options 
   * @returns {string} toastId
   */
  static show(options = {}) {
    this.ensureContainer();
    const id = 'toast_' + Math.random().toString(36).substring(2, 9);
    const toast = document.createElement('div');
    toast.id = id;
    toast.className = `qbit-toast qbit-toast-${options.type || 'info'}`;
    
    // 设置毛玻璃风格与渐变
    Object.assign(toast.style, {
      padding: '0.875rem 1.25rem',
      background: 'rgba(15, 23, 42, 0.85)',
      backdropFilter: 'blur(12px)',
      webkitBackdropFilter: 'blur(12px)',
      borderLeft: `4px solid ${this.getColor(options.type)}`,
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
      color: '#ffffff',
      fontSize: '0.875rem',
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      pointerEvents: 'auto',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      opacity: '0',
      transform: 'translateY(-10px)'
    });

    const icon = this.getIcon(options.type);
    const textNode = document.createElement('div');
    textNode.style.flex = '1';
    textNode.textContent = options.message;

    toast.appendChild(icon);
    toast.appendChild(textNode);

    if (options.txHash) {
      const link = document.createElement('a');
      let explorerUrl = 'https://explorer.mg.qbitchain.io';
      try {
        const config = getConfig();
        const network = config.chainNetwork || 'mainnet';
        const netConfig = CHAIN_NETWORKS[network];
        if (netConfig && netConfig.explorerUrl) {
          explorerUrl = netConfig.explorerUrl;
        }
      } catch (e) {
        if (QBitEcosystem.environment !== 'production') {
          explorerUrl = 'https://explorer.devnet.qbitchain.io';
        }
      }
      link.href = `${explorerUrl.replace(/\/$/, '')}/tx/${options.txHash}`;
      link.target = '_blank';
      link.textContent = '🔗 链上查看';
      Object.assign(link.style, {
        color: '#3b82f6',
        textDecoration: 'none',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        marginLeft: '0.5rem'
      });
      toast.appendChild(link);
    }

    this.container.appendChild(toast);
    
    // 渐显动画
    setTimeout(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    }, 50);

    // 自动销毁时间
    if (options.duration !== 0 && options.type !== 'pending') {
      setTimeout(() => {
        this.dismiss(id);
      }, options.duration || 3000);
    }

    return id;
  }

  static update(id, options = {}) {
    const toast = document.getElementById(id);
    if (!toast) return;
    const textNode = toast.querySelector('div');
    if (textNode && options.message) {
      textNode.textContent = options.message;
    }
    if (options.type) {
      toast.className = `qbit-toast qbit-toast-${options.type}`;
      toast.style.borderLeft = `4px solid ${this.getColor(options.type)}`;
    }
  }

  static dismiss(id) {
    const toast = document.getElementById(id);
    if (!toast) return;
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(-10px)';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }

  static getColor(type) {
    switch (type) {
      case 'success': return '#10b981'; // 翠绿
      case 'warning': return '#f59e0b'; // 珊瑚橙
      case 'error': return '#ef4444';   // 宝石红
      case 'pending': return '#3b82f6'; // 宝石蓝
      default: return '#6b7280';
    }
  }

  static getIcon(type) {
    const el = document.createElement('span');
    el.style.fontSize = '1.125rem';
    switch (type) {
      case 'success': el.textContent = '✅'; break;
      case 'warning': el.textContent = '⚠️'; break;
      case 'error': el.textContent = '❌'; break;
      case 'pending':
        el.textContent = '⚙️';
        el.style.display = 'inline-block';
        el.style.animation = 'qbit-spin 1.5s linear infinite';
        break;
      default: el.textContent = 'ℹ️'; break;
    }
    return el;
  }
}

// 动态载入 CSS 关键帧动画
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes qbit-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes qbit-shake {
      0%, 100% { transform: translateX(0); }
      20%, 60% { transform: translateX(-6px); }
      40%, 80% { transform: translateX(6px); }
    }
    .qbit-shake-active {
      animation: qbit-shake 0.4s ease-in-out;
    }
  `;
  document.head.appendChild(style);
}

/**
 * QBit 统一收银台 (Ecosystem Cashier UI overlay)
 */
export class QBitCashier {
  static activeOverlay = null;

  /**
   * 一键拉起生态统一收银台
   * @param {Object} options 
   */
  static open(options = {}) {
    if (this.activeOverlay) {
      this.close();
    }

    const overlay = document.createElement('div');
    overlay.className = 'qbit-sdk-overlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      background: 'rgba(15, 23, 42, 0.65)',
      backdropFilter: 'blur(16px)',
      webkitBackdropFilter: 'blur(16px)',
      zIndex: '9999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, system-ui, sans-serif'
    });

    const modal = document.createElement('div');
    modal.className = 'qbit-sdk-modal';
    Object.assign(modal.style, {
      background: 'rgba(30, 41, 59, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '1.25rem',
      padding: '2rem',
      width: '100%',
      maxWidth: '420px',
      color: '#f8fafc',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
    });

    // 订单信息标题
    modal.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem;">
        <h3 style="margin:0; font-size:1.25rem; font-weight:700;">QBit 统一收银台</h3>
        <button id="qbit-cashier-close" style="background:none; border:none; color:#94a3b8; cursor:pointer; font-size:1.25rem;">✕</button>
      </div>
      <div style="background:rgba(255,255,255,0.05); padding:1rem; border-radius:0.75rem; margin-bottom:1.5rem;">
        <div style="font-size:0.75rem; color:#94a3b8; margin-bottom:0.25rem;">支付金额</div>
        <div style="font-size:2rem; font-weight:800; color:#3b82f6;">${options.amount} QBIT</div>
        <div style="font-size:0.875rem; margin-top:0.5rem; border-top:1px solid rgba(255,255,255,0.05); padding-top:0.5rem; color:#e2e8f0;">
          ${options.goodsName}
        </div>
      </div>
      
      <div style="margin-bottom:1.5rem;">
        <label style="display:block; font-size:0.75rem; color:#94a3b8; margin-bottom:0.5rem;">请输入6位快捷支付密码 (云端托管钱包)</label>
        <div id="qbit-pin-wrapper" style="display:flex; gap:0.5rem; justify-content:space-between; margin-bottom:0.75rem;">
          <input type="password" maxlength="1" class="qbit-pin-input" style="width:3rem; height:3.5rem; text-align:center; font-size:1.5rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.15); background:rgba(15,23,42,0.5); color:white; outline:none;" />
          <input type="password" maxlength="1" class="qbit-pin-input" style="width:3rem; height:3.5rem; text-align:center; font-size:1.5rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.15); background:rgba(15,23,42,0.5); color:white; outline:none;" />
          <input type="password" maxlength="1" class="qbit-pin-input" style="width:3rem; height:3.5rem; text-align:center; font-size:1.5rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.15); background:rgba(15,23,42,0.5); color:white; outline:none;" />
          <input type="password" maxlength="1" class="qbit-pin-input" style="width:3rem; height:3.5rem; text-align:center; font-size:1.5rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.15); background:rgba(15,23,42,0.5); color:white; outline:none;" />
          <input type="password" maxlength="1" class="qbit-pin-input" style="width:3rem; height:3.5rem; text-align:center; font-size:1.5rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.15); background:rgba(15,23,42,0.5); color:white; outline:none;" />
          <input type="password" maxlength="1" class="qbit-pin-input" style="width:3rem; height:3.5rem; text-align:center; font-size:1.5rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.15); background:rgba(15,23,42,0.5); color:white; outline:none;" />
        </div>
      </div>
      
      <button id="qbit-pay-btn" style="width:100%; height:3rem; border:none; border-radius:0.75rem; background:linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); color:white; font-size:1rem; font-weight:700; cursor:pointer; transition: opacity 0.2s; display:flex; align-items:center; justify-content:center; gap:0.5rem;">
        确认支付
      </button>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    this.activeOverlay = overlay;

    // PIN 输入框聚焦与一键清空联动逻辑 (RULE[user_global] 6.2)
    const inputs = modal.querySelectorAll('.qbit-pin-input');
    const pinWrapper = modal.querySelector('#qbit-pin-wrapper');
    const payBtn = modal.querySelector('#qbit-pay-btn');

    inputs.forEach((input, index) => {
      // 获得焦点时全选清空，遵守清空规则
      input.addEventListener('focus', () => {
        input.value = '';
      });

      // 输入联动
      input.addEventListener('input', (e) => {
        if (input.value.length === 1 && index < inputs.length - 1) {
          inputs[index + 1].focus();
        }
      });

      // 键盘删除键退格
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && input.value === '' && index > 0) {
          inputs[index - 1].focus();
        }
      });
    });

    // 首次自动聚焦第一个输入框
    setTimeout(() => {
      if (inputs[0]) inputs[0].focus();
    }, 100);

    // 关闭回调
    modal.querySelector('#qbit-cashier-close').addEventListener('click', () => {
      this.close();
      if (options.onCancel) options.onCancel();
    });

    // 支付提交 (带阻尼特效)
    payBtn.addEventListener('click', async () => {
      const pin = Array.from(inputs).map(i => i.value).join('');
      if (pin.length < 6) {
        // 密码位数不足，晃动特效并一键清空
        pinWrapper.classList.add('qbit-shake-active');
        inputs.forEach(i => i.value = '');
        inputs[0].focus();
        setTimeout(() => pinWrapper.classList.remove('qbit-shake-active'), 400);
        return;
      }

      // 开启阻尼防抖：禁用交互，防止多次支付请求
      payBtn.disabled = true;
      payBtn.style.pointerEvents = 'none';
      payBtn.style.opacity = '0.6';
      payBtn.textContent = '正在签署交易...';

      try {
        // 请求托管签名与扣款
        const receipt = await QBitEcosystem.requestNode('/wallet/quantabit/pay-custody', {
          method: 'POST',
          body: JSON.stringify({
            amount: options.amount,
            payee: options.payeeAddress,
            order_id: options.orderId,
            pin: pin
          })
        });

        this.close();
        if (options.onSuccess) {
          options.onSuccess({
            success: true,
            txHash: receipt.tx_hash,
            payee: options.payeeAddress,
            amount: options.amount,
            timestamp: Date.now()
          });
        }
      } catch (err) {
        // 解除阻尼，重新让用户操作，并一键清空刚才输错的密码
        payBtn.disabled = false;
        payBtn.style.pointerEvents = 'auto';
        payBtn.style.opacity = '1';
        payBtn.textContent = '确认支付';
        
        // 震动警示并清空
        pinWrapper.classList.add('qbit-shake-active');
        inputs.forEach(i => i.value = '');
        inputs[0].focus();
        setTimeout(() => pinWrapper.classList.remove('qbit-shake-active'), 400);

        QBitToast.show({ message: `支付失败: ${err.message || '网络或密码异常'}`, type: 'error' });
        if (options.onError) options.onError(err.message);
      }
    });
  }

  static close() {
    if (this.activeOverlay && this.activeOverlay.parentNode) {
      this.activeOverlay.parentNode.removeChild(this.activeOverlay);
      this.activeOverlay = null;
    }
  }
}

/**
 * 链上存证高级操作器 (QBit Actions Layer)
 */
export class QBitActions {
  /**
   * 自动探测可用签名通道并调起存证广播
   * @param {AnchorOptions} options 
   */
  static async anchorResource(options = {}) {
    const toastId = QBitToast.show({ message: `准备对 ${options.title || '资源'} 进行链上作者权存证...`, type: 'pending' });

    try {
      // 1. 发起存证请求到后端分配区块打包资源
      const response = await QBitEcosystem.requestNode('/wallet/quantabit/anchor', {
        method: 'POST',
        body: JSON.stringify({
          namespace: options.namespace,
          resource_id: options.resourceId,
          author_did: options.authorDid,
          content_hash: options.contentHash,
          fee_mode: options.feeMode || 'user_pay'
        })
      });

      QBitToast.update(toastId, {
        message: '存证哈希签名确认中...',
        type: 'pending'
      });

      // 2. 模拟链上最终确认轮询 (2秒)
      await new Promise(resolve => setTimeout(resolve, 2000));

      QBitToast.update(toastId, {
        message: '🎉 存证成功锁定，区块打包完成！',
        type: 'success',
        txHash: response.tx_hash
      });

      return {
        success: true,
        txHash: response.tx_hash,
        blockNumber: response.block_number
      };
    } catch (err) {
      QBitToast.update(toastId, {
        message: `链上存证失败: ${err.message}`,
        type: 'error'
      });
      return {
        success: false,
        error: err.message
      };
    }
  }

  /**
   * 转账与代币支付快捷通道
   * @param {PaymentOptions} options 
   */
  static async requestPayment(options = {}) {
    return new Promise((resolve) => {
      QBitCashier.open({
        orderId: options.memo || `pay_${Date.now()}`,
        amount: options.amount,
        goodsName: options.title,
        payeeAddress: options.toAddress,
        onSuccess: (receipt) => resolve(receipt),
        onCancel: () => resolve({ success: false, error: '用户取消支付' }),
        onError: (err) => resolve({ success: false, error: err })
      });
    });
  }
}

/**
 * 链上回执异步校验工具
 */
export class QBitVerify {
  /**
   * 直连 QBit 链验证存证内容哈希真实性
   */
  static async verifyMemo(txHash, expectedNamespace, expectedHash) {
    try {
      // 请求 RPC 节点获取交易体内的 Memo 数据
      const txData = await QBitEcosystem.requestNode(`/wallet/quantabit/transactions/${txHash}`);
      
      if (!txData) {
        return { isConfirmed: false, isValid: false, confirmations: 0 };
      }

      // 核对 Namespace 及原始内容哈希摘要是否完全相符
      const blockchainMemo = txData.memo || '';
      const expectedMemoString = `${expectedNamespace}:${expectedHash}`;
      const isValid = blockchainMemo.trim() === expectedMemoString.trim();

      return {
        isConfirmed: txData.confirmations >= 1,
        isValid: isValid,
        signer: txData.signer || 'unknown',
        timestamp: txData.timestamp || Date.now(),
        confirmations: txData.confirmations || 1
      };
    } catch (e) {
      console.error('[QBit SDK] 异步校验链哈希失败:', e);
      return { isConfirmed: false, isValid: false, confirmations: 0 };
    }
  }
}
